/**
 * Service for persisting individual launch monitor shots (C2 + C4).
 *
 * Separate from launchMonitorImportService on purpose: that one describes what
 * the parser read, this one describes what the user confirmed. Before this
 * existed, per-shot data was discarded after review and only the profile
 * aggregate survived, so a measured carry could not be told apart from a guess.
 *
 * Uses the same axios instance (JWT interceptor lives in recommendationService).
 */

import axios from 'axios';

const API_BASE = `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000'}/api/v1`;

export type FieldSource = 'ocr' | 'manual' | 'corrected' | 'derived';
export type ShotSource = 'ocr' | 'manual' | 'csv';
export type SourceUnits = 'imperial' | 'metric';
export type Environment = 'indoor' | 'outdoor';

export interface ShotFieldInput {
  value: number;
  confidence?: number;
  source?: FieldSource;
  source_label?: string;
  /** What the parser first read, when the user changed it. */
  original_value?: number;
  correction_rule?: string;
}

export interface ShotConditions {
  device_brand: string;
  device_model?: string;
  source_units: SourceUnits;
  environment: Environment;
  /** Required outdoors — carry is not interpretable without it. */
  altitude_ft?: number;
  temperature_f?: number;
}

export interface SaveShotRequest {
  club_type: 'driver' | 'iron' | 'wedge';
  fields: Record<string, ShotFieldInput>;
  conditions: ShotConditions;
  session_id?: string;
  import_id?: string;
  shot_index?: number;
  captured_at?: string;
  club_label?: string;
  club_loft?: number;
  source?: ShotSource;
  extraction_method?: string;
  device_template?: string;
  ocr_confidence?: number;
  is_aggregate?: boolean;
  profile_id?: number;
  source_image_ref?: string;
  notes?: string;
  /** X7, §6.3: attaches the shot to a specific build. Bay mode only. */
  head_id?: number;
  shaft_id?: number;
  shaft_flex?: string;
  is_baseline?: boolean;
  fitting_id?: string;
}

export interface SaveShotResponse {
  id: number;
  session_id: string;
  is_aggregate: boolean;
  mapped_fields: Record<string, string>;
  raw_field_keys: string[];
  corrected_fields: Record<string, { from: number; to: number; rule: string }>;
  warnings: string[];
  head_id?: number | null;
  shaft_id?: number | null;
  shaft_flex?: string | null;
  is_baseline?: boolean;
  fitting_id?: string | null;
}

export async function saveLaunchMonitorShot(
  payload: SaveShotRequest,
): Promise<SaveShotResponse> {
  const response = await axios.post<SaveShotResponse>(
    `${API_BASE}/advanced/launch-monitor/save-shot`,
    payload,
  );
  return response.data;
}

export interface SaveShotBatchResponse {
  session_id: string;
  saved: number;
  shots: SaveShotResponse[];
}

export async function saveLaunchMonitorShots(
  shots: SaveShotRequest[],
  sessionId?: string,
): Promise<SaveShotBatchResponse> {
  const response = await axios.post<SaveShotBatchResponse>(
    `${API_BASE}/advanced/launch-monitor/save-shots`,
    { shots, session_id: sessionId },
  );
  return response.data;
}

// --- X7 bay mode: read back one fitting's shots grouped by build ------------

export interface FittingBuildSummary {
  head_id: number | null;
  shaft_id: number | null;
  shaft_flex: string | null;
  n_shots: number;
  club_speed_mph: number | null;
  ball_speed_mph: number | null;
  smash_factor: number | null;
  launch_angle_deg: number | null;
  spin_rate_rpm: number | null;
  carry_yds: number | null;
  total_yds: number | null;
  shot_ids: number[];
}

export interface FittingShotsResponse {
  fitting_id: string;
  profile_id: number | null;
  club_type: string;
  baseline: FittingBuildSummary | null;
  builds: FittingBuildSummary[];
}

export async function getFittingShots(fittingId: string): Promise<FittingShotsResponse> {
  const response = await axios.get<FittingShotsResponse>(
    `${API_BASE}/advanced/launch-monitor/fitting/${fittingId}`,
  );
  return response.data;
}

export function newFittingId(): string {
  return crypto.randomUUID();
}

/**
 * Build the per-field payload from the reviewed values.
 *
 * `edited` holds what the user accepted; `parsed` holds what the parser read.
 * Where they differ we send both, so the row records a correction instead of
 * quietly replacing the original reading.
 */
export function buildShotFields(
  edited: Record<string, number | undefined>,
  parsed: Record<string, { value: number; confidence?: number; source_label?: string }>,
): Record<string, ShotFieldInput> {
  const out: Record<string, ShotFieldInput> = {};
  for (const [key, value] of Object.entries(edited)) {
    if (value === undefined || Number.isNaN(value)) continue;
    const original = parsed[key];
    const field: ShotFieldInput = { value, source: original ? 'ocr' : 'manual' };
    if (original) {
      field.confidence = original.confidence;
      field.source_label = original.source_label;
      if (original.value !== value) {
        field.original_value = original.value;
        field.correction_rule = 'user_edit';
      }
    }
    out[key] = field;
  }
  return out;
}
